/**
 * Runtime validator for dictionary entries (src/data/dictionary.json).
 *
 * Schema:
 *   {
 *     word:        string, non-empty (the join key against grade items)
 *     definition:  string, may be empty (= "stub awaiting content")
 *     example:     string, may be empty
 *     synonyms:    string[], may be empty
 *     family:      string[], may be empty
 *     etymology:   string, may be empty
 *   }
 *
 * Validation is strict on shape (rejects unexpected types) but lenient on
 * content — empty `definition` is allowed because the catalogue ships
 * fully stubbed and gets filled in incrementally.
 */

/**
 * Normalize a word for case- + accent-insensitive lookup. Greek tonal
 * marks are removed via NFD decomposition (the same regex as the stub
 * generator).
 */
export function normalizeWordKey(word) {
  if (typeof word !== 'string') return '';
  return word
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');
}

export function validateDictionaryEntry(entry) {
  const issues = [];
  if (!entry || typeof entry !== 'object') return ['entry is not an object'];

  if (typeof entry.word !== 'string' || entry.word.trim().length === 0) {
    issues.push('word is missing or empty');
  }

  if (entry.definition !== undefined && typeof entry.definition !== 'string') {
    issues.push('definition must be a string (use "" for stubs)');
  }

  if (entry.example !== undefined && typeof entry.example !== 'string') {
    issues.push('example must be a string');
  }

  for (const arrayKey of ['synonyms', 'family']) {
    if (entry[arrayKey] !== undefined) {
      if (!Array.isArray(entry[arrayKey])) {
        issues.push(`${arrayKey} must be an array of strings`);
      } else {
        for (const s of entry[arrayKey]) {
          if (typeof s !== 'string' || s.trim().length === 0) {
            issues.push(`${arrayKey} contains an invalid entry`);
            break;
          }
        }
      }
    }
  }

  if (entry.etymology !== undefined && typeof entry.etymology !== 'string') {
    issues.push('etymology must be a string');
  }

  return issues;
}

/**
 * Validate the entire dictionary pool. Returns:
 *   { issues: { [word]: string[] }, duplicateKeys: string[] }
 *
 * `duplicateKeys` are normalized keys with more than one entry — these
 * indicate content collision and should be merged manually.
 */
export function validateDictionaryPool(entries) {
  const issues = {};
  const seen = new Map();
  const duplicateKeys = [];

  if (!Array.isArray(entries)) {
    return { issues: { '<root>': ['dictionary must be a JSON array'] }, duplicateKeys };
  }

  for (const entry of entries) {
    const entryIssues = validateDictionaryEntry(entry);
    const label = entry?.word ?? '<no-word>';
    if (entryIssues.length > 0) {
      issues[label] = entryIssues;
    }
    if (typeof entry?.word === 'string') {
      const key = normalizeWordKey(entry.word);
      if (key) {
        if (seen.has(key)) {
          duplicateKeys.push(key);
        } else {
          seen.set(key, entry);
        }
      }
    }
  }
  return { issues, duplicateKeys };
}

/**
 * Build the runtime lookup Map. Pure helper used by data/index.js so the
 * loader can do O(1) `getDictionaryEntry(word)`.
 */
export function buildLookupMap(entries) {
  const map = new Map();
  if (!Array.isArray(entries)) return map;
  for (const e of entries) {
    if (!e || typeof e.word !== 'string') continue;
    const key = normalizeWordKey(e.word);
    if (key && !map.has(key)) map.set(key, e);
  }
  return map;
}

/**
 * True when an entry has been filled in with at least a definition (the
 * minimum content for it to be useful to the UI).
 */
export function isPopulatedEntry(entry) {
  return typeof entry?.definition === 'string' && entry.definition.trim().length > 0;
}
